(ns frontend.ui.component.lp-portal.report.controller
  (:require
   [clojure.edn :as edn]
   [common.entity :as entity]
   [common.localization :as loc]
   [frontend.ui.page.lp-portal.report.util :as ru]))

(defn with-footnote
  [{:keys [dynamic-data dynamic-path]}
   footnote-key
   figures-data]
  (let [content-path (concat dynamic-path [footnote-key])
        completed-path (concat dynamic-path [:completed-ts footnote-key])]
    {:rich-content      (get-in dynamic-data content-path)
     :rich-content-completed-at (get-in dynamic-data completed-path)
     :rich-content-completed-path completed-path
     :rich-content-path content-path
     :figures           figures-data
     :rich-content-key footnote-key}))

(defn with-custom-title
  "Opts a card into being renamed by the report editor, overriding the title
   hardcoded in the view. Cards not threaded through this keep their default.

   Titles get their own `:custom-titles` sub-map so they never collide with the
   footnote string stored directly under `title-key`. Clearing a title stores
   an empty string rather than dropping the key, so consumers must test blank,
   not nil."
  [{:keys [dynamic-data dynamic-path]} title-key card-data]
  (let [custom-title-path (concat dynamic-path [:custom-titles title-key])]
    (assoc card-data
           :custom-title      (get-in dynamic-data custom-title-path)
           :custom-title-path custom-title-path)))

(defn rich-content
  [{:keys [dynamic-data dynamic-path]}
   path-key]
  (let [content-path (concat dynamic-path [path-key])
        completed-path (concat dynamic-path [:completed-ts path-key])]
    {:rich-content (get-in dynamic-data content-path)
     :rich-content-completed-at (get-in dynamic-data completed-path)
     :rich-content-completed-path completed-path
     :rich-content-path content-path
     :rich-content-key path-key}))

(defn image-content
  [{:keys [dynamic-data dynamic-path]} footnote-key _image-id]
  (let [content-path (concat dynamic-path [footnote-key])
        completed-path (concat dynamic-path [:completed-ts footnote-key])
        image-path (concat dynamic-path [:image-id footnote-key])]
    {:rich-content (get-in dynamic-data content-path)
     :rich-content-completed-at (get-in dynamic-data completed-path)
     :rich-content-completed-path completed-path
     :rich-content-path content-path
     :rich-image-path image-path
     :image (get-in dynamic-data image-path)}))

;;TODO in the future the image will be saved to S3 instead of base64 directly.
;; this is a temporary solution for the Resi V2 report, refactor/rename when generalizing this

(defn image-content-resi-v2
  [{:keys [dynamic-report-images dynamic-path]} footnote-key _image-id]
  (let [content-path (concat dynamic-path [footnote-key])
        completed-path (concat dynamic-path [:completed-ts footnote-key])
        image-path (concat dynamic-path [:image-id footnote-key])]
    {:rich-content (get-in dynamic-report-images content-path)
     :rich-content-completed-at (get-in dynamic-report-images completed-path)
     :rich-content-completed-path completed-path
     :rich-content-path content-path
     :rich-image-path image-path
     :image (get-in dynamic-report-images image-path)}))

(defn optional-rich-content
  [{:keys [dynamic-data dynamic-path]}
   heading-key
   content-key]
  (let [content-path (concat dynamic-path [content-key])
        heading-path (concat dynamic-path [heading-key])
        enabled-path (concat dynamic-path [:enabled?])
        completed-path (concat dynamic-path [:completed-ts content-key])]
    {:rich-content      (get-in dynamic-data content-path)
     :rich-content-path content-path
     :rich-content-completed-at (get-in dynamic-data completed-path)
     :rich-content-completed-path completed-path
     :heading-path      heading-path
     :heading-text      (get-in dynamic-data heading-path)
     :enabled-path      enabled-path
     :enabled?          (= "true" (get-in dynamic-data enabled-path))}))

(defn manual-data-set-label
  [dynamic-data dynamic-path chart-key accounts]
  (let [updated-by (get-in dynamic-data (concat dynamic-path [chart-key :manual-data-by]))
        updated-on (get-in dynamic-data (concat dynamic-path [chart-key :manual-data-on]))]
    (when (or updated-by updated-on)
      (loc/tr :manual-data-set-option-label
              [(or (entity/display-string (->> accounts
                                               (filter (fn [account]
                                                         (= (:db/id account) updated-by)))
                                               (first))) ;;is there a better way to display the account name?
                   (loc/tr :system))
               (ru/->date-dd-MM-yyyy updated-on)]))))

(defn imported-data-set-label
  [dynamic-data accounts]
  (let [imported-by (:imported-by dynamic-data)
        imported-on (:imported-on dynamic-data)]
    (when (or imported-by imported-on)
      (loc/tr :imported-data-set-option-label
              [(or (entity/display-string (->> accounts
                                               (filter (fn [account]
                                                         (= (:db/id account) imported-by)))
                                               (first))) ;;is there a better way to display the account name?
                   (loc/tr :system))
               (ru/->date-dd-MM-yyyy imported-on)]))))

(defn previous-data-set-label
  [dynamic-data dynamic-path chart-key]
  (let [previous-data-set-reference-date (get-in dynamic-data [:previous-data-set-reference-date])
        published-on (get-in dynamic-data [:previous-data-set-published-on])
        previous-data (get-in dynamic-data (concat dynamic-path [chart-key :previous-data]))]
    (when previous-data
      (loc/tr :previous-data-set-option-label
              [(ru/->date-dd-MM-yyyy previous-data-set-reference-date)
               (ru/->date-dd-MM-yyyy published-on)]))))


(defn with-editable-charts
  [{:keys [dynamic-data dynamic-path accounts multi-chart?] :as props}
   chart-key chart-config config->editable-data editable-data->raw-data
   config]
  (let [saved-selected-set (get-in dynamic-data (concat dynamic-path [chart-key :selected-data-set]))
        selected-set (or (cond (string? saved-selected-set)
                               (edn/read-string saved-selected-set)
                               (keyword? saved-selected-set)
                               saved-selected-set)
                         (when (and (:figures config)
                                    (or (:imported-by dynamic-data)
                                        (:imported-on dynamic-data)))
                           :imported-data-set)
                         (when (get-in dynamic-data (concat dynamic-path [chart-key :previous-data]))
                           :previous-data-set)
                         (when (:figures config)
                           :imported-data-set)
                         :manual-data-set)
        raw-data (case selected-set
                   :manual-data-set
                   (get-in dynamic-data (concat dynamic-path [chart-key :manual-data]))
                   :previous-data-set
                   (get-in dynamic-data (concat dynamic-path [chart-key :previous-data]))
                   :imported-data-set
                   (:figures config)
                   (:figures config))
        raw-data (if (string? raw-data)
                   (edn/read-string raw-data)
                   raw-data)
        config-data (get-in dynamic-data (concat dynamic-path [chart-key :manual-chart-configs]))
        config-data-edn (if (string? config-data)
                          (edn/read-string config-data)
                          config-data)
        chart-data (cond (and multi-chart? config-data-edn)
                         (assoc config-data-edn
                                :series
                                (->> config-data-edn
                                     :series
                                     (map-indexed
                                      (fn [idx series]
                                        (assoc series :data (get-in raw-data [:series idx :data]))))
                                     (vec))
                                :series-categories (:series-categories raw-data))
                         config-data-edn
                         (merge config-data-edn raw-data)
                         :else
                         raw-data)
        chart-config (chart-config chart-data
                                   (when (:custom-configs-arg props) (:custom-configs-arg props)))]
    (merge config
           {:figures chart-config
            :raw-data raw-data
            :previous-data-set-reference-date (:previous-data-set-reference-date dynamic-data)
            :imported-data-set-label (imported-data-set-label dynamic-data accounts)
            :previous-data-set-label (previous-data-set-label dynamic-data dynamic-path chart-key)
            :manual-data-set-label (manual-data-set-label dynamic-data dynamic-path chart-key accounts)
            :manual-data-set-path (concat dynamic-path [chart-key :manual-data])
            :manual-chart-configs-path (concat dynamic-path [chart-key :manual-chart-configs])
            :manual-chart-configs config-data-edn
            :selected-data-set selected-set
            :selected-data-set-path (concat dynamic-path [chart-key :selected-data-set])
            :chart-config->editable-data config->editable-data
            :editable-data->raw-data editable-data->raw-data
            :fixed-columns? (:fixed-columns? props)
            :fixed-labels? (:fixed-labels? props)
            :donut-chart? (:donut-chart? props)
            :multi-chart? (:multi-chart? props)})))

(defn with-editable-tables
  [{:keys [dynamic-data dynamic-path accounts] :as props}
   table-key raw-data->editable-data editable-data->raw-data table-data]
  (let [saved-selected-set (get-in dynamic-data (vec (concat dynamic-path [table-key :selected-data-set])))
        selected-set (or (cond (string? saved-selected-set)
                               (edn/read-string saved-selected-set)
                               (keyword? saved-selected-set)
                               saved-selected-set)
                         (when (and (:figures table-data)
                                    (or (:imported-by dynamic-data)
                                        (:imported-on dynamic-data)))
                           :imported-data-set)
                         (when (get-in dynamic-data [table-key :previous-data])
                           :previous-data-set)
                         (when (:figures table-data)
                           :imported-data-set)
                         :manual-data-set)
        raw-data (case selected-set
                   :manual-data-set
                   (get-in dynamic-data (vec (concat dynamic-path [table-key :manual-data])))
                   :previous-data-set
                   (get-in dynamic-data (vec (concat dynamic-path [table-key :previous-data])))
                   :imported-data-set
                   (:figures table-data)
                   (:figures table-data))
        raw-data (if (string? raw-data)
                   (edn/read-string raw-data)
                   raw-data)]
    (merge table-data
           {:figures raw-data
            :previous-data-set-reference-date (:previous-data-set-reference-date dynamic-data)
            :imported-data-set-label (imported-data-set-label dynamic-data accounts)
            :previous-data-set-label (previous-data-set-label dynamic-data dynamic-path table-key)
            :manual-data-set-label (manual-data-set-label dynamic-data dynamic-path table-key accounts)
            :manual-data-set-path (concat dynamic-path [table-key :manual-data])
            :selected-data-set selected-set
            :selected-data-set-path (concat dynamic-path [table-key :selected-data-set])
            :raw-data->editable-data raw-data->editable-data
            :editable-data->raw-data editable-data->raw-data
            :fixed-columns? (:fixed-columns? props)
            :fixed-labels? (:fixed-labels? props)
            :fixed-rows? (:fixed-rows? props)
            :disable-heading-edit? (:disable-heading-edit? props)})))
